import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StaticSegmentsService {

  private readonly blacklist = ['edit', 'new', 'update'];

  constructor(private router: Router) {}

  private extractCleanSegments(route: ActivatedRoute): string[] {
    const segments: string[] = [];
    let current: ActivatedRoute | null = route;
    while (current) {
      const path = current.routeConfig?.path;
      if (path) {
        const parts = path.split('/');
        for (const part of parts) {
          if (
            part &&
            !part.startsWith(':') &&
            !this.blacklist.includes(part)
          ) {
            segments.push(part);
          }
        }
      }
      current = current.firstChild;
    }
    return segments;
  }

  getCleanPathSnapshot(route: ActivatedRoute): string {
    const segments = this.extractCleanSegments(route);
    return '/' + segments.join('/');
  }

  getCleanLinkArraySnapshot(route: ActivatedRoute): any[] {
    const segments = this.extractCleanSegments(route);
    return ['/', ...segments];
  }

  getCleanPath$(rootRoute: ActivatedRoute): Observable<string> {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.getCleanPathSnapshot(rootRoute))
    );
  }

  getCleanLinkArray$(rootRoute: ActivatedRoute): Observable<any[]> {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.getCleanLinkArraySnapshot(rootRoute))
    );
  }

  pathExistsInConfig(segments: string[]): boolean {
    const matchPath = (routes: any[], pathIndex: number): boolean => {
      for (const route of routes) {
        const pathParts = (route.path ?? '').split('/').filter(Boolean);

        let i = 0;
        while (i < pathParts.length && pathIndex + i < segments.length) {
          const routePart = pathParts[i];
          const targetPart = segments[pathIndex + i];

          if (routePart.startsWith(':')) {
            i++;
            continue;
          }

          if (routePart !== targetPart) {
            break;
          }

          i++;
        }

        if (i === pathParts.length) {
          const nextIndex = pathIndex + i;

          if (nextIndex === segments.length) {
            return true;
          }

          if (route.children) {
            if (matchPath(route.children, nextIndex)) {
              return true;
            }
          }
        }

        if ((route.path ?? '') === '' && route.children) {
          if (matchPath(route.children, pathIndex)) {
            return true;
          }
        }
      }

      return false;
    };

    return matchPath(this.router.config, 0);
  }

  private getAllAbsolutePathsFromConfig(
    routes: any[],
    prefix: string[] = []
  ): string[][] {
    const results: string[][] = [];

    for (const route of routes) {
      const pathParts = (route.path ?? '').split('/').filter(Boolean);

      if (pathParts.length === 0) {
        if (route.children) {
          results.push(...this.getAllAbsolutePathsFromConfig(route.children, prefix));
        }
        continue;
      }

      const newPrefix = [...prefix];

      for (const part of pathParts) {
        if (part.startsWith(':')) {
          newPrefix.push(':param');  // Marker für Param-Routen
        } else {
          newPrefix.push(part);
        }
      }

      results.push(newPrefix);

      if (route.children) {
        results.push(...this.getAllAbsolutePathsFromConfig(route.children, newPrefix));
      }
    }

    return results;
  }

  findMatchingPathForEnding(desiredEnding: string[]): string[] | null {
    const allPaths = this.getAllAbsolutePathsFromConfig(this.router.config);

    for (const path of allPaths) {
      const cleanPath = path.filter(p => p !== ':param');  // Parameter ignorieren

      if (
        cleanPath.length >= desiredEnding.length &&
        cleanPath.slice(-desiredEnding.length).join('/') === desiredEnding.join('/')
      ) {
        return cleanPath;
      }
    }

    return null;
  }

  buildEditPathFromCurrent(route: ActivatedRoute): string[] {
    const segments: string[] = [];

    let current: ActivatedRoute | null = route;
    while (current) {
      const path = current.routeConfig?.path;
      if (path) {
        const parts = path.split('/');
        for (const part of parts) {
          if (
            part &&
            !part.startsWith(':') &&
            !this.blacklist.includes(part)
          ) {
            segments.push(part);
          }
        }
      }
      current = current.firstChild;
    }

    return [...segments, 'edit'];
  }

  // ANGEPASSTE METHODE: Kann jetzt mit mehrstufigen Pfaden umgehen
  getPrefixTo(path: string): string[] {
    const search = (routes: any[], prefix: string[]): string[] | null => {
      for (const route of routes) {
        const routePath = route.path ?? '';

        // Prüfe ob der aktuelle Pfad dem gesuchten Pfad entspricht
        if (routePath === path) {
          return [...prefix, path];
        }

        // Prüfe ob der aktuelle Pfad ein mehrstufiger Pfad ist, der den gesuchten Pfad enthält
        if (routePath.includes('/')) {
          const pathParts = routePath.split('/');
          if (pathParts.includes(path)) {
            return [...prefix, ...pathParts];
          }
        }

        // Rekursiv in children suchen
        if (route.children) {
          const result = search(route.children, routePath ? [...prefix, routePath] : [...prefix]);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };

    const result = search(this.router.config, []);
    if (!result) {
      throw new Error(`Route to path '${path}' not found in Router Config!`);
    }
    return result;
  }

  // NEUE METHODE: Sucht nach einem Pfad-Segment am Ende eines mehrstufigen Pfads
  getPrefixToSegment(targetSegment: string): string[] {
    const search = (routes: any[], prefix: string[]): string[] | null => {
      for (const route of routes) {
        const routePath = route.path ?? '';

        // Prüfe mehrstufige Pfade
        if (routePath.includes('/')) {
          const pathParts = routePath.split('/');
          if (pathParts[pathParts.length - 1] === targetSegment) {
            return [...prefix, routePath];
          }
        }

        // Prüfe einfache Pfade
        if (routePath === targetSegment) {
          return [...prefix, routePath];
        }

        // Rekursiv in children suchen
        if (route.children) {
          const result = search(route.children, routePath ? [...prefix, routePath] : [...prefix]);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };

    const result = search(this.router.config, []);
    if (!result) {
      throw new Error(`Route segment '${targetSegment}' not found in Router Config!`);
    }
    return result;
  }
}
